import React, { useEffect, useMemo, useRef, useState } from "react";
import api from "@/lib/api";
import { useI18n } from "@/lib/i18n";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";

// Liste complète des villes de Côte d'Ivoire (chefs-lieux et villes principales) —
// l'autocomplétion n'est PAS limitée aux villes enregistrées dans le workflow.
export const CI_CITIES = [
  "Abidjan", "Abengourou", "Aboisso", "Adiaké", "Adzopé", "Agboville", "Agnibilékrou",
  "Akoupé", "Alépé", "Anyama", "Arrah", "Ayamé", "Bangolo", "Béoumi", "Bettié",
  "Biankouma", "Bingerville", "Bloléquin", "Bocanda", "Bondoukou", "Bongouanou",
  "Bonon", "Bonoua", "Botro", "Bouaflé", "Bouaké", "Bouna", "Boundiali", "Brobo",
  "Buyo", "Dabakala", "Dabou", "Daloa", "Danané", "Daoukro", "Dianra", "Didiévi",
  "Dikodougou", "Dimbokro", "Divo", "Djékanou", "Doropo", "Duékoué", "Facobly",
  "Ferkessédougou", "Fresco", "Gagnoa", "Gbéléban", "Gohitafla", "Grand-Bassam",
  "Grand-Lahou", "Grand-Zattry", "Guéyo", "Guiglo", "Guitry", "Issia", "Jacqueville",
  "Kani", "Kaniasso", "Katiola", "Kong", "Korhogo", "Kouassi-Kouassikro", "Kouibly",
  "Koun-Fao", "Kounahiri", "Kouto", "Lakota", "M'Bahiakro", "M'Batto", "Madinani",
  "Man", "Mankono", "Méagui", "Minignan", "Nassian", "Niakaramandougou", "Odienné",
  "Ouangolodougou", "Ouaninou", "Oumé", "Prikro", "Sakassou", "San-Pédro", "Sandégué",
  "Sassandra", "Séguéla", "Séguélon", "Sikensi", "Sinématiali", "Sinfra", "Sipilou",
  "Soubré", "Tabou", "Taï", "Tanda", "Téhini", "Tengréla", "Tiapoum", "Tiassalé",
  "Tiébissou", "Tiéningboué", "Touba", "Toulepleu", "Toumodi", "Transua", "Vavoua",
  "Yakassé-Attobrou", "Yamoussoukro", "Zouan-Hounien", "Zuénoula",
];

const norm = (s) =>
  (s || "").toLowerCase().normalize("NFD").replace(/[\u0300-\u036f]/g, "").replace(/[^a-z0-9]/g, "");

// Composant UNIQUE de ville de destination — utilisé à l'identique dans
// « J'ai déjà ma marchandise » et « Je veux acheter en ligne » (Phase 3B uniforme).
// - Gestion de plusieurs villes = NON → ville principale automatique, pas de liste.
// - Gestion de plusieurs villes = OUI → recherche/autocomplétion (villes admin actives
//   + breakdown CI pour la Côte d'Ivoire), saisie manuelle possible pour une ville
//   jamais ajoutée dans Admin, villes explicitement désactivées non sélectionnables,
//   avertissement dynamique si ville finale ≠ ville principale.
export const DestCity = ({ country, value, onChange, testid = "dest-city" }) => {
  const { t } = useI18n();
  const [adminCities, setAdminCities] = useState([]);
  const code = country?.code;
  const multi = !!country?.multi_city;
  const mainCity = country?.main_city || "";
  useEffect(() => {
    if (!code || !multi) { setAdminCities([]); return; }
    api.get(`/cities?country_code=${code}&withstatus=1`)
      .then(({ data }) => setAdminCities(data)).catch(() => setAdminCities([]));
  }, [code, multi]);
  // Ville principale automatique quand la gestion multi-villes est désactivée
  useEffect(() => {
    if (country && !multi && value !== mainCity) onChange(mainCity);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [country?.name, multi, mainCity]);
  const { cities, disabledNames } = useMemo(() => {
    const off = adminCities.filter((c) => c.active === false).map((c) => c.name);
    const on = adminCities.filter((c) => c.active !== false).map((c) => c.name);
    const base = code === "CI" ? CI_CITIES : [];
    const merged = [...new Set([...base, ...on])]
      .filter((c) => !off.some((d) => norm(d) === norm(c)))
      .sort((a, b) => a.localeCompare(b, "fr"));
    return { cities: merged, disabledNames: off };
  }, [adminCities, code]);
  if (!country) return null;
  if (!multi) {
    return (
      <div data-testid={`${testid}-box`}>
        <Label>{t("dc.arrival")}</Label>
        <div className="h-9 flex items-center border border-border bg-secondary/60 px-3 text-sm font-medium rounded-sm"
          data-testid={`${testid}-fixed`}>{mainCity || country.name}</div>
      </div>
    );
  }
  return <CitySearch cities={cities} disabledNames={disabledNames} mainCity={mainCity}
    value={value} onChange={onChange} testid={testid} />;
};

// Rétrocompatibilité : préréglage Côte d'Ivoire (même composant, aucune duplication)
export const DestCityCI = ({ value, onChange, testid = "ci-city" }) => (
  <DestCity country={{ name: "Côte d'Ivoire", code: "CI", multi_city: true, main_city: "Abidjan" }}
    value={value} onChange={onChange} testid={testid} />
);

const CitySearch = ({ cities, disabledNames, mainCity, value, onChange, testid }) => {
  const { t } = useI18n();
  const [q, setQ] = useState(value || "");
  const [open, setOpen] = useState(false);
  const [manual, setManual] = useState(false);
  const blurTimer = useRef(null);
  const matches = useMemo(() => {
    const n = norm(q);
    if (!n) return [];
    const starts = cities.filter((c) => norm(c).startsWith(n));
    const contains = cities.filter((c) => !norm(c).startsWith(n) && norm(c).includes(n));
    return [...starts, ...contains].slice(0, 8);
  }, [q, cities]);
  const isDisabled = (name) => disabledNames.some((d) => norm(d) === norm(name));
  const pickCity = (c) => { setQ(c); onChange(c); setOpen(false); setManual(false); };
  const onManual = (v) => {
    setQ(v);
    onChange(isDisabled(v.trim()) ? "" : v.trim());
  };
  const blocked = q && isDisabled(q.trim());
  return (
    <div data-testid={`${testid}-box`}>
      <Label>{t("dc.arrival")}</Label>
      {!manual ? (
        <div className="relative">
          <Input data-testid={`${testid}-search`} className="rounded-sm" placeholder="Commencez à taper : Ga… → Gagnoa"
            value={q} autoComplete="off"
            onChange={(e) => { setQ(e.target.value); setOpen(true); if (value) onChange(""); }}
            onFocus={() => setOpen(true)}
            onBlur={() => { blurTimer.current = setTimeout(() => setOpen(false), 200); }} />
          {open && q && (
            <div className="absolute z-50 mt-1 w-full border border-border bg-card shadow-lg max-h-56 overflow-y-auto" data-testid={`${testid}-suggestions`}>
              {matches.map((c) => (
                <button key={c} type="button" data-testid={`${testid}-opt-${c}`}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                  onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); pickCity(c); }}>
                  {c}
                </button>
              ))}
              {matches.length === 0 && !blocked && norm(q) && (
                <button type="button" data-testid={`${testid}-use-typed`}
                  className="block w-full px-3 py-2 text-left text-sm hover:bg-secondary"
                  onMouseDown={(e) => { e.preventDefault(); clearTimeout(blurTimer.current); pickCity(q.trim()); }}>
                  Utiliser « {q.trim()} »
                </button>
              )}
              {matches.length === 0 && blocked && <p className="px-3 py-2 text-sm text-muted-foreground">Aucune ville trouvée</p>}
            </div>
          )}
        </div>
      ) : (
        <Input data-testid={`${testid}-manual`} className="rounded-sm" placeholder="Saisissez le nom de votre ville"
          value={q} autoComplete="off" onChange={(e) => onManual(e.target.value)} />
      )}
      <button type="button" data-testid={`${testid}-not-listed`}
        className="mt-1 text-xs underline text-muted-foreground hover:text-foreground"
        onClick={() => { setManual(!manual); setOpen(false); setQ(""); onChange(""); }}>
        {manual ? "Revenir à la recherche de villes" : "Ma ville n'apparaît pas"}
      </button>
      {blocked && (
        <div className="mt-2 border border-red-300 bg-red-50 p-2 text-xs text-red-700" data-testid={`${testid}-unavailable`}>
          {t("dc.unavailable")}
        </div>
      )}
      {value && mainCity && norm(value) !== norm(mainCity) && (
        <div className="mt-2 border border-amber-300 bg-amber-50 p-2 text-xs text-amber-800" data-testid={`${testid}-warning`}>
          <b>{t("dc.warnTitle")} {mainCity}</b><br />
          {t("dc.warnBody1")} {mainCity}. {t("dc.warnBody2")}
        </div>
      )}
    </div>
  );
};
